import { IPopupProps } from '@/components/Common/DesignSystem/Popup/Popup';
import {
  RAFFLE_DEAL_LOGIN,
  RAFFLE_DEAL_LOGOUT,
  RAFFLE_DEAL_PAGE_NAME,
  RAFFLE_DEAL_PAGE_TYPE,
  RAFFLE_DEAL__ERROR_MAP,
} from '@/components/RaffleDeal/constant';
import { IRaffleDealV3HistoryPayload } from '@/models/RaffleDeal/interfaces/IRaffleDealHistoryPayload';
import { IRaffleDealPromotionData } from '@/models/RaffleDeal/interfaces/IRaffleDealPromotionsPayload';
import { IRaffleDealV3WinHistoryPayload } from '@/models/RaffleDeal/interfaces/IRaffleDealWinHistoryPayload';
import { IRaffleDealWinner } from '@/models/RaffleDeal/interfaces/IRaffleDealWinnersPayload';
import { RaffleDealActions } from '@/models/RaffleDeal/RaffleDealActions';
import { RaffleDealRequestBuilder } from '@/models/RaffleDeal/RaffleDealRequestBuilder';
import { IRaffleDealProps } from '@/pages/raffle-deal/[group]';
import { Kinesis } from '@/services/TrackerService';
import extractAPIData from '@/utils/functions/extractAPIdata';
import { getYaNative } from '@yanolja-org/ya-native';
import { action, computed, observable } from 'mobx';
import moment, { Moment } from 'moment-timezone';
import Router from 'next/router';
import { identity } from 'ramda';
import { createContext } from 'react';

import GlobalStore from './GlobalStore';

export enum EN_RAFFLE_DEAL_TAB {
  DRAW = '야놀자드로우', // enum은 변경이 어려워 이 값을 사용하는 RaffleDealTab에서 직접 store 값으로 변경
  HISTORY = '내역',
}
export type TRaffleDealTab = keyof typeof EN_RAFFLE_DEAL_TAB;

export enum EN_RAFFLE_DEAL_SUB_TAB {
  ALL = '전체',
  APPLY = '응모내역',
  WIN = '당첨내역',
}
export type TRaffleDealSubTab = keyof typeof EN_RAFFLE_DEAL_SUB_TAB;

interface IRaffleDealStoreProps extends IRaffleDealProps {
  promotionData: IRaffleDealPromotionData;

  globalStore: GlobalStore;
}
export class RaffleDealStore {
  @observable groupCode: string;
  @observable isOpenShareModal = false;
  @observable activeTab: TRaffleDealTab = 'DRAW';
  @observable activeSubTab: TRaffleDealSubTab = 'ALL';

  @observable isOpenSuccessPopup = false;
  @observable popupProps: Omit<IPopupProps, 'isOpen'> = {
    children: '',
    mainBtn: {
      label: '',
      onClick: (): void => void 0,
    },
  };

  @observable isLoggedIn: boolean;
  @observable now: Moment;
  @observable historyData: IRaffleDealV3HistoryPayload | null;
  @observable winHistoryData: IRaffleDealV3WinHistoryPayload | null;
  @observable winners: IRaffleDealWinner[]; // 딜 별 당첨자 목록

  @observable totalPages: number; // 전체 페이지 갯수
  @observable totalPageChapter: number; // 전체 페이지 챕터
  @observable currentPageChapter: number = 1; // 현재 노출되는 페이지 챕터
  @observable currentPage: number = 1; // 현재 페이지

  private errorCode: string;
  private readonly globalStore: GlobalStore;
  private readonly promotionData: IRaffleDealPromotionData;
  private readonly actions = new RaffleDealActions(RaffleDealRequestBuilder);

  constructor({
    groupCode,
    promotionData,
    isLoggedIn,
    activeTab,
    activeSubTab,
    now,
    historyData,
    winHistoryData,
    globalStore,
  }: IRaffleDealStoreProps) {
    this.groupCode = groupCode;
    this.globalStore = globalStore;
    this.promotionData = promotionData;
    this.activeTab = activeTab;
    this.activeSubTab = activeSubTab;
    this.isLoggedIn = isLoggedIn;
    this.now = moment(now);
    this.historyData = historyData;
    this.winHistoryData = winHistoryData;

    this.totalPages =
      this.activeSubTab === 'WIN' ? this.winHistoryData?.totalPages || 0 : this.historyData?.totalPages || 0;
    this.totalPageChapter = Math.ceil(this.totalPages / 5);
  }

  get PromotionData(): IRaffleDealPromotionData {
    return this.promotionData;
  }

  get PromotionTitle(): string {
    return this.promotionData?.promotion?.title || 'NOL드로우';
  }

  @computed
  get CouponUrl(): string {
    return `${window.location.origin}/mypage/coupon`;
  }

  @action.bound
  setNow(now: Moment): void {
    this.now = now;
  }

  @action.bound
  openShareModal(): void {
    Kinesis.click(RAFFLE_DEAL_PAGE_NAME, {
      object: '공유하기 버튼 클릭',
      pageType: RAFFLE_DEAL_PAGE_TYPE,
      promotionName: `${this.PromotionTitle} 프로모션`,
      promotionDetail: this.groupCode,
      CustomerStatus: this.isLoggedIn ? RAFFLE_DEAL_LOGIN : RAFFLE_DEAL_LOGOUT,
    });
    this.isOpenShareModal = true;
  }

  @action.bound
  closeShareModal(): void {
    this.isOpenShareModal = false;
  }

  @action.bound
  setActiveTab(tab: TRaffleDealTab): void {
    Kinesis.click(RAFFLE_DEAL_PAGE_NAME, {
      object: tab === 'DRAW' ? `${this.PromotionTitle} 탭 클릭` : '내역 탭 클릭',
      pageType: RAFFLE_DEAL_PAGE_TYPE,
      promotionName: `${this.PromotionTitle} 프로모션`,
      promotionDetail: this.groupCode,
      CustomerStatus: this.isLoggedIn ? RAFFLE_DEAL_LOGIN : RAFFLE_DEAL_LOGOUT,
    });

    this.activeTab = tab;
    this.setActiveSubTab('ALL');

    Router.replace({
      pathname: Router.route,
      query: {
        group: Router.query.group,
        tab: this.activeTab,
        ...(this.activeTab === 'HISTORY' && { subTab: this.activeSubTab }),
      },
    });
  }

  @action.bound
  setActiveSubTab(subTab: TRaffleDealSubTab): void {
    Kinesis.click(RAFFLE_DEAL_PAGE_NAME, {
      object: subTab === 'ALL' ? '전체 탭 클릭' : subTab === 'APPLY' ? '응모내역 클릭' : '당첨내역 클릭',
      pageType: RAFFLE_DEAL_PAGE_TYPE,
      promotionName: `${this.PromotionTitle} 프로모션`,
      promotionDetail: this.groupCode,
      CustomerStatus: this.isLoggedIn ? RAFFLE_DEAL_LOGIN : RAFFLE_DEAL_LOGOUT,
    });
    this.activeSubTab = subTab;

    Router.replace({
      pathname: Router.route,
      query: {
        group: Router.query.group,
        tab: this.activeTab,
        subTab: this.activeSubTab,
      },
    });
  }

  @action.bound
  closePopupAndReload(): void {
    this.isOpenSuccessPopup = false;
    location.reload();
  }

  @action.bound
  openPopup(props: Omit<IPopupProps, 'isOpen'>): void {
    this.popupProps = props;
    this.isOpenSuccessPopup = true;
  }

  @action.bound
  goToLogin(redirectUrl: string): void {
    const yaNative = getYaNative();

    if (yaNative.isEnabled) {
      yaNative.login();
    } else {
      window.location.href = `/login?redirect=${redirectUrl}`;
    }
  }

  @action.bound
  goToWinnerForm(formUrl: string): void {
    const yaNative = getYaNative();

    if (yaNative.isEnabled) {
      yaNative.wv({
        url: formUrl,
        layout: { modal: true, title: this.PromotionTitle, modalBottomNavi: false },
      });
    } else {
      window.open(formUrl, '_blank');
    }
  }

  @action.bound
  goToCoupon(): void {
    const yaNative = getYaNative();

    if (yaNative.isEnabled) {
      yaNative.deepLink({ link: `yanoljamotel://wv?url=${this.CouponUrl}` });
    } else {
      window.location.href = this.CouponUrl;
    }
  }

  /** 응모 내역 조회하기 (전체 or 응모한 내역) */
  @action.bound
  async getHistory(tab: TRaffleDealSubTab): Promise<void> {
    const commonParams = {
      query: {
        page: this.currentPage,
        size: 10,
        requestAt: this.now.format('YYYY-MM-DDTHH:mm:ss'),
      },
      resources: {
        groupCode: this.groupCode,
      },
    };

    const res = await extractAPIData(
      tab === 'ALL' ? this.actions.getAllHistory(commonParams) : this.actions.getParticipationHistory(commonParams),
      identity,
      (err) => {
        this.errorCode = err.data?.code || 'SERVE_ERROR';
        return null;
      },
    );

    if (res) {
      this.historyData = res;
      this.totalPages = this.historyData?.totalPages || 0;
      this.totalPageChapter = Math.ceil(this.totalPages / 5);
    } else {
      this.globalStore.toast.addItem({
        key: 'raffledeal-participate-history-error',
        text: RAFFLE_DEAL__ERROR_MAP[this.errorCode],
        timeout: 5000,
      });
    }
  }

  /** 당첨 내역 조회하기 */
  @action.bound
  async getWinHistory(): Promise<void> {
    const res = await extractAPIData(
      this.actions.getWinHistory({
        query: {
          page: this.currentPage,
          size: 10,
          requestAt: this.now.format('YYYY-MM-DDTHH:mm:ss'),
        },
        resources: {
          groupCode: this.groupCode,
        },
      }),
      identity,
      (err) => {
        this.errorCode = err.data?.code || 'SERVE_ERROR';
        return null;
      },
    );

    if (res) {
      this.winHistoryData = res;
      this.totalPages = this.winHistoryData?.totalPages || 0;
      this.totalPageChapter = Math.ceil(this.totalPages / 5);
    } else {
      this.globalStore.toast.addItem({
        key: 'raffledeal-win-history-error',
        text: RAFFLE_DEAL__ERROR_MAP[this.errorCode],
        timeout: 5000,
      });
    }
  }

  /**
   * 당첨자 목록 조회하기
   */
  @action.bound
  async getWinners({ promotionId, dealId }: { promotionId: number; dealId: number }): Promise<void> {
    const res = await extractAPIData(
      this.actions.getWinners({
        resources: {
          promotionId: String(promotionId),
          dealId: String(dealId),
        },
        query: {
          requestAt: this.now.format('YYYY-MM-DDTHH:mm:ss'),
        },
      }),
      identity,
      (err) => {
        this.errorCode = err.data?.code || 'SERVE_ERROR';
        return null;
      },
    );

    if (res) {
      this.winners = res.winners || [];
    } else {
      this.winners = [];
      this.globalStore.toast.addItem({
        key: 'raffledeal-winners-error',
        text: RAFFLE_DEAL__ERROR_MAP[this.errorCode],
        timeout: 5000,
      });
    }
  }

  /** 페이징 처리 */
  // 한 챕터에 랜더링할 페이지 갯수
  @computed
  get RenderPageCount(): number {
    if (this.currentPageChapter === this.totalPageChapter && this.totalPages % 5 > 0) {
      return this.totalPages % 5;
    } else if (this.totalPages === 0) {
      return 0;
    }
    return 5;
  }

  // 다음 페이지 챕터
  @action.bound
  clickNext(): void {
    this.currentPageChapter += 1;
    this.currentPage = (this.currentPageChapter - 1) * 5 + 1;
  }

  // 이전 페이지 챕터
  @action.bound
  clickPrev(): void {
    this.currentPageChapter -= 1;
    this.currentPage = (this.currentPageChapter - 1) * 5 + 1;
  }

  @computed
  get DisabledPrev(): boolean {
    if (this.currentPageChapter === 1) {
      return true;
    }
    return false;
  }

  @computed
  get DisabledNext(): boolean {
    if (this.currentPageChapter === this.totalPageChapter) {
      return true;
    }
    return false;
  }

  @action.bound
  setCurrentPage(page: number): void {
    this.currentPage = page;
  }

  @action.bound
  resetPagination(): void {
    this.currentPageChapter = 1;
    this.currentPage = 1;
  }
}

export const RaffleDealStoreCtx = createContext<RaffleDealStore>(null!);
